import { useLazyQuery, useMutation } from "@apollo/client";
import { ALLOWED_THUMBNAIL_EXTENSIONS, THEME } from "@constants/global";
import {
  Checkbox,
  LiquidPillGroup,
  ModalButtons,
  Textarea,
  type LiquidPillGroupItem,
} from "components/ui/v2";
import type { FormV2Props } from "components/ui/v2/form-v2-props";
import { useDrawer } from "context/drawer/drawer.provider";
import { useModal } from "context/modal/modal.provider";
import { gqlFeedback } from "gql";
import { ICreateFeedbackInput } from "gql/Feedback/mutations";
import { useTheme } from "next-themes";
import useTranslation from "next-translate/useTranslation";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useState, useRef, useLayoutEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { EFeedbackType, IFeedBack } from "types/Feedback.type";
import useSound from "use-sound";
import { UploadCloud, X } from "lucide-react";
import { putToSignedUrl } from "utils/storage-upload";

export const FEEDBACK_FORM_ID = "new-feedback-form";

const ModalFeedback = ({
  hideFooter = false,
  onFooterChange,
  onClose,
}: FormV2Props = {}) => {
  const { asPath } = useRouter();
  const { closeDrawer } = useDrawer();
  const { closeModal } = useModal();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const close = () => {
    closeDrawer();
    onCloseRef.current?.();
    closeModal();
  };
  const onFooterChangeRef = useRef(onFooterChange);
  onFooterChangeRef.current = onFooterChange;
  const [type, setType] = useState<EFeedbackType>(EFeedbackType.BUG);
  const [playFeedback] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25,
  });
  const [terms, setTerms] = useState<boolean>(false);
  const [playError] = useSound("/sounds/denied-sound.mp3", { volume: 0.25 });
  const { t } = useTranslation("common");
  const { theme } = useTheme();
  const [screnshoot, setScrenshoot] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [composeLoading, setComposeLoading] = useState(false);

  const {
    handleSubmit: handleCreateFeedback,
    control: feedbackControl,
    watch,
    formState: { errors },
  } = useForm<ICreateFeedbackInput>();
  const [createFeedback, { loading: mutationLoading }] = useMutation<
    { createFeedback: IFeedBack },
    { createFeedbackInput: ICreateFeedbackInput }
  >(gqlFeedback.mutations.CREATE_FEEDBACK, {
    onCompleted(response) {
      const {
        createFeedback: {
          user: { name },
        },
      } = response;
      playFeedback();
      setComposeLoading(false);
      toast.success(`${t("newFeedback.success")} ${name}`);
      close();
    },
    onError(e) {
      toast.error(e.message);
      playError();
    },
  });

  const [signedUrl] = useLazyQuery(gqlFeedback.queries.GENERATE_SIGNED_URL, {
    fetchPolicy: "network-only",
  });

  const handleCancel = () => {
    close();
  };

  const commentValue = watch("comment");

  useLayoutEffect(() => {
    const publish = onFooterChangeRef.current;
    if (!hideFooter || !publish) return;

    publish(
      <ModalButtons
        compact
        loading={mutationLoading || composeLoading}
        submitLabel={t("newFeedback.submit")}
        disabled={commentValue === ""}
        onCancel={() => close()}
        submitButtonProps={{ form: FEEDBACK_FORM_ID, type: "submit" }}
      />
    );

    return () => publish(null);
    // Do not depend on close/onClose — new identity each render would republish forever.
  }, [commentValue, composeLoading, hideFooter, mutationLoading, t]);

  const typeItems: LiquidPillGroupItem[] = [
    { value: EFeedbackType.BUG, label: t("newFeedback.problem"), variant: "rose" },
    { value: EFeedbackType.IDEA, label: t("newFeedback.idea"), variant: "gold" },
    { value: EFeedbackType.OTHER, label: t("newFeedback.other"), variant: "slate" },
  ];

  const handleCreate = async (data: ICreateFeedbackInput) => {
    setComposeLoading(true);
    try {
      let mdUrl = "";

      if (screnshoot) {
        const {
          data: { getSignedUrl: getSignedUrlMd },
        } = await signedUrl({
          variables: {
            key: screnshoot.name,
          },
        });

        mdUrl = await putToSignedUrl(
          getSignedUrlMd,
          screnshoot,
          screnshoot.type || ""
        );
      }

      await createFeedback({
        variables: {
          createFeedbackInput: {
            comment: data.comment,
            pathname: asPath,
            screenshot: mdUrl,
            type,
            terms,
          },
        },
      });
    } catch (error) {
      setComposeLoading(false);
      toast.error(t("newFeedback.error"));
    }
  };

  return (
    <form
      id={FEEDBACK_FORM_ID}
      onSubmit={handleCreateFeedback(handleCreate)}
      className="flex w-full flex-col gap-5 px-1 py-1"
    >
      <div className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400">
          {t("newFeedback.selectType")}
        </span>
        <div className="inline-flex rounded-full bg-white/40 backdrop-blur-[2px] p-0.5 dark:bg-zinc-900/40 border border-slate-200/40 dark:border-zinc-800/40 shadow-[0_1px_2px_rgba(15,23,42,0.02)]">
          <LiquidPillGroup
            items={typeItems}
            activeValue={type}
            onChange={(value) => setType(value as EFeedbackType)}
            size="sm"
          />
        </div>
      </div>

      <Controller
        name="comment"
        control={feedbackControl}
        defaultValue=""
        rules={{ required: true }}
        render={({ field }) => (
          <Textarea
            {...field}
            label={t("newFeedback.question")}
            placeholder={t("newFeedback.placeholder")}
            rows={5}
            error={errors.comment ? t("newFeedback.mandatory") : undefined}
            helperText={t("newFeedback.questionWarning")}
          />
        )}
      />

      <div className="space-y-2">
        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400">
          {t("newFeedback.screenshootLabel")}
        </label>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={ALLOWED_THUMBNAIL_EXTENSIONS}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            setScrenshoot(file);
          }}
        />

        {screnshoot ? (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200/70 bg-slate-50/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
            <span className="truncate text-[9px] text-slate-700 dark:text-zinc-300">
              {screnshoot.name}
            </span>
            <button
              type="button"
              className="rounded-full p-1 text-slate-500 hover:bg-slate-200/80 dark:hover:bg-zinc-800"
              onClick={() => {
                setScrenshoot(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              aria-label={t("Quitar")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex h-[150px] w-full cursor-pointer flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-white/70 p-4 text-center transition-colors hover:bg-slate-50 dark:border-zinc-800 dark:bg-zinc-900/60 dark:hover:bg-zinc-900"
          >
            <Image
              alt=""
              src={
                theme === THEME.dark
                  ? "/svg/illustrations/upload-icon.svg"
                  : "/svg/illustrations/upload-icon-light.svg"
              }
              height={54}
              width={54}
            />
            <span className="mt-2 max-w-xs text-[9px] text-slate-500 dark:text-zinc-400">
              <span className="font-semibold text-indigo-600 dark:text-indigo-300">
                {t("newFeedback.screenshootDescription1")}
              </span>{" "}
              {t("newFeedback.screenshootDescription2")}
            </span>
            <span className="mt-2 inline-flex items-center gap-1 text-[9px] font-medium text-indigo-600 dark:text-indigo-300">
              <UploadCloud className="h-3.5 w-3.5" />
              {t("o haz clic para seleccionar archivos")}
            </span>
          </button>
        )}
      </div>

      <Checkbox
        checked={terms}
        onCheckedChange={(checked) => setTerms(checked === true)}
        label={t("newFeedback.terms")}
      />

      {!hideFooter ? (
        <ModalButtons
          loading={mutationLoading || composeLoading}
          submitLabel={t("newFeedback.submit")}
          disabled={watch("comment") === ""}
          onCancel={handleCancel}
        />
      ) : null}
    </form>
  );
};

export default ModalFeedback;
